import React, { useEffect, useState } from "react";
import { Keyboard, Animated, Pressable, StatusBar } from "react-native";
import Clipboard from "@react-native-community/clipboard";
import {
  MainTitle,
  Box,
  MainText,
  MainButton,
  PressableOpacity,
} from "../../components/common";
import { useDispatch, useSelector } from "react-redux";
import {
  changeSignupInput,
  checkMagicCodeRequest,
} from "@microdoing/shared/services/signup/actions";
import {
  changeEmail,
  changePassword,
  loginRequest,
} from "@microdoing/shared/services/login/actions";
import i18n from "@microdoing/shared/i18n";
import sanitizeName from "../../utils/sanitizeName";
import sanitizeAccessCode from "../../utils/sanitizeAccessCode";
import InputCode from "./InputCode";
import {
  getSignupAccessCode,
  getSignupEmail,
  getSignupError,
  getSignupPassword,
  getSignupRequesting,
} from "@microdoing/shared/services/signup/selectors";
import { useNavigation, useRoute } from "@react-navigation/native";
import HeaderScreen from "app/components/common/HeaderScreen";
import KeyboardAvoidWrapper from "app/components/common/KeyboardAvoidWrapper";
import normalize from "app/utils/normalize";
import theme from "app/Theme/Theme";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import WebSignup from "./WebSignup";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const CodeImage = require("../../../assets/images/magic-code.png");
const IMAGE_HEIGHT = normalize(120);
const IMAGE_HEIGHT_SMALL = normalize(80);

const Signup = () => {
  const [hasAddButton, setHasAddButton] = useState(true);

  const requesting = useSelector(getSignupRequesting);
  const email = useSelector(getSignupEmail);
  const password = useSelector(getSignupPassword);
  const accessCode = useSelector(getSignupAccessCode);
  const error = useSelector(getSignupError);

  const insets = useSafeAreaInsets();

  const dispatch = useDispatch();
  const navigation = useNavigation();
  const route = useRoute();

  useEffect(() => {
    const accessCodeFromParams = route?.params?.accessCode;

    if (!!accessCodeFromParams) {
      dispatch(changeSignupInput({ accessCode: accessCodeFromParams }));
    }
  }, []);

  const onChangeAccessCode = async accessCode => {
    // detect that a code has been pasted with the format "ABCD-EFGF" : " - "
    let cleanAccessCode = "";
    const specialCharPosition = accessCode.indexOf("-");
    if (specialCharPosition < accessCode.length && specialCharPosition > -1) {
      const clipboardContent = await Clipboard.getString();
      cleanAccessCode = clipboardContent
        .replace(/[^a-z0-9]/gi, "")
        .toUpperCase();
    } else {
      cleanAccessCode = accessCode.replace(/[^a-z0-9]/gi, "").toUpperCase();
    }
    dispatch(changeSignupInput({ accessCode: cleanAccessCode }));
  };

  const goNext = () => {
    // Apple testing
    if (email === "clement@pulse-experience.co") {
      dispatch(
        loginRequest({
          email: sanitizeName(email),
          password: password,
        }),
      );
      Keyboard.dismiss();
    } else {
      const cleanCode = sanitizeAccessCode(accessCode);
      dispatch(checkMagicCodeRequest({ accessCode: cleanCode }));
    }
  };

  const goToLogin = () => {
    dispatch(changeEmail(email));
    dispatch(changePassword(password));
    navigation.navigate("Login");
  };

  if (isWebDesktop) {
    return (
      <WebSignup
        onChangeAccessCode={onChangeAccessCode}
        goNext={goNext}
        goToLogin={goToLogin}
      />
    );
  }

  return (
    <Box
      flex={1}
      paddingVertical="xxs"
      backgroundColor="neutral.white"
      flexGrow={1}
      style={{ marginTop: insets.top }}
    >
      <Box marginLeft="xm">
        <HeaderScreen title="" leftIcon />
      </Box>
      <StatusBar backgroundColor={theme.colors["neutral.white"]} />

      <KeyboardAvoidWrapper>
        <Box overflow="visible" marginHorizontal="xm">
          <Box alignItems="center">
            <Animated.Image
              source={CodeImage}
              resizeMode="contain"
              style={[
                { height: IMAGE_HEIGHT_SMALL, width: IMAGE_HEIGHT_SMALL },
              ]}
            />
            <MainTitle variant="l7-bold" marginBottom="xs" marginTop="l">
              {i18n.t("join-campaign")}
            </MainTitle>
            <MainText variant="l5-medium" color="neutral.500" marginBottom="l">
              {i18n.t("enter-magic-code")}
            </MainText>
            <InputCode
              style={styles.input}
              value={accessCode}
              setValue={onChangeAccessCode}
              hasAddButton={hasAddButton}
              setHasAddButton={() => setHasAddButton(false)}
            />
            {error === "Invalid code" && (
              <MainText
                variant="l3-medium"
                color="system.red.950"
                marginRight="m"
                textAlign="left"
              >
                {i18n.t("signup-companyNotFound")}
              </MainText>
            )}
          </Box>
          <Box marginTop="s">
            <MainButton
              testID="signup-magic-code-submit"
              text={i18n.t("validate")}
              onPress={goNext}
              isDisabled={!accessCode || accessCode.length < 8}
              isLoading={requesting}
            />
            <PressableOpacity onPress={goToLogin}>
              <Box flexDirection="row" marginTop="xs" justifyContent="center">
                <MainText
                  variant="l4-medium"
                  color="neutral.800"
                  marginRight="xxs"
                >
                  {i18n.t("got-an-account")}
                </MainText>
                <MainText variant="l4-semibold" color="brand.500">
                  {i18n.t("i-login")}
                </MainText>
              </Box>
            </PressableOpacity>
          </Box>
        </Box>
      </KeyboardAvoidWrapper>
    </Box>
  );
};

const styles = {
  input: {
    height: normalize(50),
    borderRadius: isWebDesktop ? normalize(8) : normalize(4),
    marginBottom: theme.spacing.s,
  },
};

export default Signup;
